import styled from "@emotion/styled";

import { useCustomTranslation } from "@/utils/useCustomTranslation";

import { InfoText } from "@/components";
import {
  CourseByStudentResponseDto,
  CourseProcessStatusEnum,
  CourseResponseDto,
  CourseTypeEnum,
} from "@shared/generated/api/model";
import { getInstructorName } from "@/utils/inquiry";
import { useRouter } from "next/router";
import { Button } from "@shared/lib/components/common/atom/button";
import { BUTTON_TYPE } from "@shared/lib/styles/theme";
import { openToast, TOAST_TYPE } from "@shared/lib/utils/toast";

import { useState } from "react";
import { useAccessToken } from "@/hooks/auth/use-access-token";

export const MyClassCard = ({
  course,
  isLiked,
  handleLink,
}: {
  course: CourseResponseDto | CourseByStudentResponseDto;
  isLiked?: boolean | false;
  handleLink?: () => void;
}) => {
  const [isActionLoading, setIsActionLoading] = useState(false);
  const { accessToken } = useAccessToken();

  const { t } = useCustomTranslation();
  const router = useRouter();
  const locale = router.locale as "ko" | "en";

  const { title, thumbnailImage, type, cycles, instructor, totalSessions } =
    course;
  const { nearScheduleId, joinUrl } = course as CourseByStudentResponseDto;

  const refCycle = cycles[0];
  let isCycleOngoing = true;
  let isOpenForEnrollment = false;

  if (!refCycle || refCycle.status === CourseProcessStatusEnum.PLANNED) {
    isCycleOngoing = false;
    if (refCycle?.selling === true && !refCycle.enrollmentClosed) {
      isOpenForEnrollment = true;
    }
  }

  const isCycleFinished =
    refCycle?.status === CourseProcessStatusEnum.COMPLETED;

  const isLinkActive = isLiked
    ? isOpenForEnrollment || isCycleOngoing || isOpenForEnrollment
    : (nearScheduleId &&
        !isCycleFinished &&
        (isOpenForEnrollment || isCycleOngoing || isOpenForEnrollment)) ||
      false;

  const handleStartClass = async () => {
    if (!accessToken) {
      openToast(TOAST_TYPE.ERROR, t("login_required"));
      return;
    }

    setIsActionLoading(true);
    try {
      if (joinUrl) {
        window.open(joinUrl, "_blank", "noopener,noreferrer");
      } else {
        throw new Error(t("cannot_enter_classroom"));
      }
    } catch (error) {
      console.error("수업 시작 중 오류 발생:", error);
      openToast(TOAST_TYPE.ERROR, t("cannot_start_class"));
    } finally {
      setIsActionLoading(false);
    }
  };

  return (
    <CardWrapper
      onClick={isLiked ? handleLink : undefined}
      isLinkActive={isLiked ? isLinkActive : false}
    >
      <ThumbnailWrapper isLinkActive={isLinkActive}>
        <img src={thumbnailImage} />
      </ThumbnailWrapper>
      <ContentWrapper>
        <ClassTypeText type={type}>
          {type === CourseTypeEnum.SINGLE
            ? t("one_day")
            : t("multiple_sessions")}
        </ClassTypeText>
        <InfoText
          isLiked={isLiked}
          isCycleOngoing={isCycleOngoing}
          isCycleFinished={isCycleFinished}
          isOpenForEnrollment={isOpenForEnrollment}
          type={type}
          refCycle={refCycle}
          totalSessions={totalSessions ?? 0}
        />
        <ClassTitleText>
          {title.length > 30 ? `${title.slice(0, 30)}...` : title}
        </ClassTitleText>
        <TeacherText>{getInstructorName(course, locale)}</TeacherText>
        {!isLiked && (
          <EnterButton
            type="button"
            styleType={BUTTON_TYPE.FILL}
            disabled={
              isActionLoading || !isLinkActive || (!joinUrl && !isLinkActive)
            }
            onClick={joinUrl ? handleStartClass : handleLink}
          >
            {!joinUrl || isActionLoading
              ? isLinkActive
                ? t("go_to_class_waiting_room")
                : t("class_is_over")
              : t("enter_classroom")}
          </EnterButton>
        )}
      </ContentWrapper>
    </CardWrapper>
  );
};

const CardWrapper = styled.div<{ isLinkActive?: boolean | true }>`
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  max-height: 360px;
  cursor: ${({ isLinkActive }) => (isLinkActive ? "pointer" : "default")};
  box-shadow: 0px 0px 9.645px 0px rgba(0, 0, 0, 0.2);
`;

const ThumbnailWrapper = styled.div<{ isLinkActive?: boolean | true }>`
  height: 153px;
  opacity: ${({ isLinkActive }) => (isLinkActive ? 1 : 0.5)};
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px 8px 0 0;
  }
`;

const ContentWrapper = styled.div`
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ClassTypeText = styled.div<{ type: string }>`
  ${({ theme }) => theme.typography.body1.b};
  color: ${({ type, theme }) =>
    type === CourseTypeEnum.SINGLE
      ? theme.colors.sub.orange.default
      : theme.colors.sub.green.default};
`;

const ClassTitleText = styled.div`
  ${({ theme }) => theme.typography.body1.b};
  min-height: 44px;
`;

const TeacherText = styled.div`
  ${({ theme }) => theme.typography.body2.m};
  color: ${({ theme }) => theme.colors.text.medium.black};
`;

const EnterButton = styled(Button)`
  margin-top: 8px;
  &.btn-${BUTTON_TYPE.FILL.toLowerCase()} {
    ${({ theme }) => theme.typography.body2.b};
    color: ${({ theme }) => theme.colors.text.high.white};
    padding: 8px 16px;
    border-radius: 8px;
    &:disabled {
      background-color: ${({ theme }) => theme.colors.mui.text.disabled};
      font-weight: 500;
    }
  }
`;
